import React, {useState} from 'react';
import Button from '@mui/material/Button';
import {Box, Container, TextField, Typography} from '@mui/material';

import {actionCreators} from '../../../../store/poctoolsContentManagement/poctoolsContentManagementActions';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {ApplicationState} from '../../../../store';
import DropZone from '../../generalPurpose/DropZone';
import {useNavigate} from 'react-router-dom';
import {createNewPOCTool} from '../helper/createPOCTool';
import {editPOCTool} from '../helper/editPOCTool';
import Loader from '../../../POCWebPortal/generalPurpose/Loader';
import HeaderBar from '../helper/POCToolCreatorHeaderBar';

type Props = {
  goNextStage: Function;
  setIsPOCToolChanged: Function;
  isPOCToolChanged: Boolean;
  handleQuite: Function;
};

export default function POCToolSettingsForm(props: Props) {
  const {goNextStage, setIsPOCToolChanged, isPOCToolChanged, handleQuite} =
    props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const pocToolSummary = useAppSelector(
    (state: ApplicationState) =>
      state.poctoolsContentManagementState.pointOfCareTool,
  );
  const displayPocToolContent = useAppSelector(
    state => state.poctoolsContentManagementState.displayPocToolContent,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [nameValidation, setNameValidation] = useState({
    isValid: true,
    errorText: '',
  });
  const [descriptionValidation, setDescriptionValidation] = useState({
    isValid: true,
    errorText: '',
  });
  const [imageValidation, setImageValidation] = useState({
    isValid: true,
    errorText: '',
  });

  const handleContinues = event => {
    if (!validateInputs()) return;
    return handleSave(event, true);
  };

  const setPOCToolSettingInStore = (
    name,
    description,
    image,
    POCToolCreatedId = 'NewPOCTool',
  ) => {
    dispatch(
      actionCreators.setPOCToolSummary({
        id: POCToolCreatedId,
        name,
        description,
        image: {
          file: image?.file || '',
          fileName: image?.fileName || '',
          url: image?.url || '',
        },
      }),
    );
  };
  const logChange = () => {
    if (!isPOCToolChanged) {
      setIsPOCToolChanged(true);
    }
  };
  const handleSetThumbnail = (file, fileName) => {
    logChange();
    setPOCToolSettingInStore(
      pocToolSummary.name,
      pocToolSummary.description,
      {
        file: file,
        fileName: fileName,
      },
      pocToolSummary.id,
    );
  };
  const handleNameChange = event => {
    logChange();
    setPOCToolSettingInStore(
      event.target.value,
      pocToolSummary.description,
      pocToolSummary.image,
      pocToolSummary.id,
    );
  };
  const handleDescriptionChange = event => {
    logChange();
    setPOCToolSettingInStore(
      pocToolSummary.name,
      event.target.value,
      pocToolSummary.image,
      pocToolSummary.id,
    );
  };

  const validateInputs = () => {
    let result = true;
    if (!pocToolSummary.name) {
      setNameValidation({
        isValid: false,
        errorText: 'The name is required.',
      });
      result = false;
    }
    if (!pocToolSummary.description) {
      setDescriptionValidation({
        isValid: false,
        errorText: 'The description is required.',
      });
      result = false;
    }
    if (pocToolSummary.description.length > 200) {
      setDescriptionValidation({
        isValid: false,
        errorText: "Description length can't be more than 200.",
      });
      result = false;
    }
    if (!pocToolSummary.image.file && !pocToolSummary.image.url) {
      setImageValidation({
        isValid: false,
        errorText: 'Image is required.',
      });
      result = false;
    }
    if (result) {
      setNameValidation({isValid: true, errorText: ''});
      setDescriptionValidation({isValid: true, errorText: ''});
      setImageValidation({isValid: true, errorText: ''});
    }
    return result;
  };
  const onCreatePOCTool = (POCToolCreatedId, isContinue) => {
    if (!POCToolCreatedId) {
      return alert('error with saving POCTool');
    }
    setPOCToolSettingInStore(
      pocToolSummary.name,
      pocToolSummary.description,
      pocToolSummary.image,
      POCToolCreatedId,
    );
    setIsPOCToolChanged(false);

    if (isPOCToolChanged) {
      setTimeout(() => {
        navigate(`/EditPOCTool/:${POCToolCreatedId}`);
        setIsSubmitted(false);
        if (isContinue) {
          return goNextStage();
        } else return;
      }, 1500);
    } else {
      setIsSubmitted(false);
      if (isContinue) {
        return goNextStage();
      } else return;
    }
  };

  const handleSave = async (event, isContinue = false) => {
    event.preventDefault();
    if (!validateInputs()) return;
    try {
      if (!pocToolSummary.id || pocToolSummary.id == 'NewPOCTool') {
        //if it's new POCTool/SuperPOCTool
        return createNewPOCTool({
          pocToolSummary: pocToolSummary,
          displayPocToolContent: displayPocToolContent || [],
          setIsLoading,
          setIsSubmitted,
          navigate,
        }).then(POCToolCreatedId =>
          onCreatePOCTool(POCToolCreatedId, isContinue),
        );
      } else {
        if (isPOCToolChanged) {
          //update old POCTool/SuperPOCTool

          return editPOCTool({
            pocToolSummary: pocToolSummary,
            displayPocToolContent,
            id: pocToolSummary.id,
            setIsLoading,
            setIsSubmitted,
            navigate,
          }).then(POCToolCreatedId =>
            onCreatePOCTool(POCToolCreatedId, isContinue),
          );
        } else {
          onCreatePOCTool(pocToolSummary.id, isContinue);
        }
      }
    } catch (err) {
      setPOCToolSettingInStore(
        pocToolSummary.name,
        pocToolSummary.description,
        pocToolSummary.image,
        'NewPOCTool',
      );
      if (err === 401) {
        navigate('/Login');
        throw err;
      }
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Loader />
      </Container>
    );
  }

  if (isSubmitted) {
    return (
      <Container style={{marginTop: '25%'}} maxWidth="md">
        <Typography variant="h4" align="center">
          Your Point-of-Care Tool is saved!
        </Typography>
      </Container>
    );
  }

  return (
    <Box width={'100%'}>
      <Box paddingBottom={2}>
        <HeaderBar title={'POC Tool Settings'} />
      </Box>
      <form style={styles.form}>
        <Box style={styles.filedContainer as React.CSSProperties}>
          <label style={styles.lable} htmlFor="name">
            Name:
          </label>
          <TextField
            error={!nameValidation.isValid}
            label={nameValidation.errorText}
            id="outlined-basic"
            variant="outlined"
            onChange={handleNameChange}
            value={pocToolSummary.name}
          />
        </Box>
        <Box style={styles.filedContainer as React.CSSProperties}>
          <label htmlFor="description" style={styles.lable}>
            Description:
          </label>
          <TextField
            error={!descriptionValidation.isValid}
            id="outlined-basic"
            label={descriptionValidation.errorText}
            variant="outlined"
            multiline={true}
            rows={4}
            onChange={handleDescriptionChange}
            value={pocToolSummary.description}
          />
        </Box>
        <Box style={styles.filedContainer as React.CSSProperties}>
          <label htmlFor="thumbnail">Image:</label>
          <DropZone
            thumbnail={pocToolSummary.image?.file}
            imageUrl={pocToolSummary.image?.url}
            setThumbnail={handleSetThumbnail}
            isValid={imageValidation.isValid}
            fileType="img"
          />
        </Box>
        <span>Maximum file size 1 MB. File permitted PNG, JPG, JPEG.</span>
        <Box style={styles.bottomButtonsContainer}>
          <Button
            variant="contained"
            style={styles.saveButton}
            onClick={() => handleQuite()}>
            Back to Dashboard
          </Button>
          <Box>
            <Button
              variant="contained"
              style={styles.saveButton}
              onClick={handleSave}>
              Save
            </Button>
            <Button variant="contained" onClick={handleContinues}>
              Save and Continue
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
}

const styles = {
  form: {width: '100%'},
  saveButton: {marginRight: 20},
  filedContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 10,
  },
  lable: {
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    color: '#09101D',
    paddingBottom: 5,
  },
  bottomButtonsContainer: {
    padding: '100px 0px 50px',
    display: 'flex',
    alignItems: 'end',
    marginTop: 32,
    width: '100%',
    justifyContent: 'space-between',
  },
};
